"use client";

/**
 * Root-layout error boundary (OBS, #1006).
 *
 * `error.tsx` boundaries (see `dashboard/error.tsx`, `admin/error.tsx`)
 * only catch errors thrown BELOW the root layout. A throw in
 * `layout.tsx` itself — or anywhere above where those boundaries mount —
 * skipped all of them and fell through to Next's blank default error
 * screen, unreported to Sentry.
 *
 * `global-error.tsx` is the one boundary Next mounts above the root
 * layout, so per Next's own convention it must render its own
 * `<html>`/`<body>` — the root layout's markup (and anything it
 * depends on, e.g. Clerk/Convex providers) is not guaranteed to be
 * intact when this renders. Keep this file's own dependency surface
 * minimal for that reason.
 *
 * Brand rule: no OpenClaw/Claude/Anthropic in customer-facing copy —
 * this page has none.
 */

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

import "@/styles/globals.css";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en" className="dark">
      <body className="flex min-h-[100dvh] items-center justify-center bg-background px-6 text-foreground">
        <div className="w-full max-w-md text-center">
          <div className="text-sm font-semibold tracking-wide text-primary">
            Raileon
          </div>
          <h1 className="mt-4 text-lg font-semibold">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We hit an unexpected error loading this page. It&apos;s been
            reported — try again, or come back in a moment.
          </p>
          {error.digest ? (
            <p className="mt-3 font-mono text-xs text-muted-foreground">
              digest {error.digest}
            </p>
          ) : null}
          <button
            type="button"
            onClick={reset}
            className="mt-6 rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted active:scale-[0.98]"
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
